import { ComponentProps } from 'react'

import {
  AnswerPageContext,
  Breadcrumb,
  BreadcrumbType,
  CategoryDictionary,
  CategoryLayout,
  FilterCategoryLayout,
  FilterSearchLayout,
  FilterSearchOnlyLayout,
  FilterSearchResultLayout,
  FrequentLayout,
  Hierarchy,
  HtmlLayout,
  IconLayout,
  IframeLayout,
  ImportantLayout,
  InformationLayout,
  Layout,
  RelatedLayout,
  SearchLayout,
  SearchPageContext,
  TagPageContext,
} from '@karakuri-web/faq-type'

import { SkillApi } from '../components'
import { value } from '../utils'

import { AnswerWidget, AnswerWidgetSettings } from './AnswerWidget'
import { BreadcrumbWidget } from './BreadcrumbWidget'
import { CategoryWidget, CategoryWidgetSettings } from './CategoryWidget'
import { FaqWidget } from './FaqWidget'
import { FilterCategoryWidget, FilterCategoryWidgetSettings } from './FilterCategoryWidget'
import { FilterSearchOnlyWidget } from './FilterSearchOnlyWidget'
import {
  FilterSearchResultWidget,
  FilterSearchResultWidgetSettings,
} from './FilterSearchResultWidget'
import { FilterSearchWidget, FilterSearchWidgetSettings } from './FilterSearchWidget'
import { HtmlWidget } from './HtmlWidget'
import { IconWidget } from './IconWidget'
import { IframeWidget } from './IframeWidget'
import { ImportantWidget, ImportantWidgetSettings } from './ImportantWidget'
import { InformationWidget, InformationWidgetSettings } from './InformationWidget'
import { NaviWidget, NaviWidgetSettings } from './NaviWidget'
import { RelatedWidget } from './RelatedWidget'
import { SearchWidget, SearchWidgetSettings } from './SearchWidget'
import { TagWidget } from './TagWidget'

interface BreadcrumbSetting {
  words?: {
    breadcrumb?: {
      top?: string | null
    } | null
  } | null
}

interface FaqPageData<T> {
  settingYaml: T
}

interface LocationWithHierarchiesState {
  state?: {
    hierarchies?: Hierarchy[]
  } | null
}

interface SearchLocation extends LocationWithHierarchiesState {
  pathname: string
}

interface LocationWithSearchAndHierarchiesState extends LocationWithHierarchiesState {
  search: string
}

interface BreadcrumbTypeSetting {
  breadcrumbType?: BreadcrumbType | null
}

type AnswerPageWidgetSettings = AnswerWidgetSettings &
  CategoryWidgetSettings &
  SearchWidgetSettings &
  NaviWidgetSettings &
  BreadcrumbTypeSetting &
  BreadcrumbSetting

type IndexPageWidgetSettings = CategoryWidgetSettings &
  SearchWidgetSettings &
  FilterSearchWidgetSettings &
  FilterCategoryWidgetSettings &
  InformationWidgetSettings &
  ImportantWidgetSettings

type SearchPageWidgetSettings = CategoryWidgetSettings &
  FilterSearchWidgetSettings &
  FilterSearchResultWidgetSettings &
  FilterCategoryWidgetSettings &
  SearchWidgetSettings &
  BreadcrumbTypeSetting &
  BreadcrumbSetting

type TagPageWidgetSettings = CategoryWidgetSettings &
  SearchWidgetSettings &
  NaviWidgetSettings &
  BreadcrumbTypeSetting &
  BreadcrumbSetting

export interface RenderAnswerPageWidgetProps<
  T extends AnswerPageWidgetSettings = AnswerPageWidgetSettings,
> {
  data: FaqPageData<T>
  location: LocationWithSearchAndHierarchiesState
  pageContext: AnswerPageContext
  path: string
  breadcrumb: Breadcrumb
  skillApi: SkillApi
  dictionary: CategoryDictionary
}

export interface RenderIndexPageWidgetProps<
  T extends IndexPageWidgetSettings = IndexPageWidgetSettings,
> {
  data: FaqPageData<T>
  path: string
}

export interface RenderSearchPageWidgetProps<
  T extends SearchPageWidgetSettings = SearchPageWidgetSettings,
> {
  data: FaqPageData<T>
  location: SearchLocation
  pageContext: SearchPageContext
  path: string
  breadcrumb: Breadcrumb
  q: string[]
  page: number
  dictionary: CategoryDictionary
  onSearch: NonNullable<ComponentProps<typeof FilterSearchWidget>['onSearch']>
}

export interface RenderTagPageWidgetProps<T extends TagPageWidgetSettings = TagPageWidgetSettings> {
  data: FaqPageData<T>
  pageContext: TagPageContext
  path: string
  breadcrumb: Breadcrumb
  hierarchies: Hierarchy[]
  dictionary: CategoryDictionary
}

function hierarchies(location: LocationWithHierarchiesState) {
  return location.state?.hierarchies || []
}

function topTitle(words?: BreadcrumbSetting['words']) {
  return words?.breadcrumb?.top || 'よくあるご質問'
}

function buildIndexSearchPageContext<T extends IndexPageWidgetSettings>(
  data: FaqPageData<T>['settingYaml']
): SearchPageContext {
  return {
    category: { id: '', sub: data.categories || [] },
    children: [],
  }
}

export function renderAnswerPageWidget<T extends AnswerPageWidgetSettings>(
  layouts: readonly Layout[],
  props: RenderAnswerPageWidgetProps<T>
) {
  return layouts.map(layout => {
    switch (layout.type) {
      case 'navi': {
        return (
          <NaviWidget
            key={layout.id}
            botId={value(props.data.settingYaml.botId, '')}
            tenantId={value(props.data.settingYaml.tenantId, '')}
            breadcrumb={props.breadcrumb}
            breadcrumbType={props.data.settingYaml.breadcrumbType}
            relatedCategories={props.pageContext.relatedCategories}
            hierarchies={hierarchies(props.location)}
            searchWaitMs={props.data.settingYaml.waitMs?.search ?? 5000}
            topTitle={topTitle(props.data.settingYaml.words)}
            searchType={props.data.settingYaml.searchType || ''}
            data={props.data}
          />
        )
      }
      case 'answer': {
        return <AnswerWidget key={layout.id} {...props} />
      }
      case 'breadcrumb': {
        return (
          <BreadcrumbWidget
            key={layout.id}
            breadcrumb={props.breadcrumb}
            breadcrumbType={props.data.settingYaml.breadcrumbType}
            relatedCategories={props.pageContext.relatedCategories}
            hierarchies={hierarchies(props.location)}
            dictionary={props.dictionary}
            topTitle={topTitle(props.data.settingYaml.words)}
          />
        )
      }
      case 'category': {
        return (
          <CategoryWidget
            key={layout.id}
            layout={layout as CategoryLayout}
            data={props.data}
            path={props.path}
          />
        )
      }
      case 'search': {
        return <SearchWidget key={layout.id} layout={layout as SearchLayout} data={props.data} />
      }
      case 'related': {
        return (
          <RelatedWidget
            key={layout.id}
            related={props.pageContext?.related || []}
            layout={layout as RelatedLayout}
          />
        )
      }
      case 'html': {
        return <HtmlWidget key={layout.id} layout={layout as HtmlLayout} />
      }
      case 'iframe': {
        return <IframeWidget key={layout.id} layout={layout as IframeLayout} />
      }
      default: {
        return null
      }
    }
  })
}

export function renderIndexPageWidget<T extends IndexPageWidgetSettings>(
  layouts: readonly Layout[],
  { data, path }: RenderIndexPageWidgetProps<T>
) {
  const pageContext = buildIndexSearchPageContext(data.settingYaml)
  return layouts.map(layout => {
    switch (layout.type) {
      case 'information': {
        return (
          <InformationWidget key={layout.id} layout={layout as InformationLayout} data={data} />
        )
      }
      case 'important': {
        return <ImportantWidget key={layout.id} layout={layout as ImportantLayout} data={data} />
      }
      case 'search': {
        return <SearchWidget key={layout.id} layout={layout as SearchLayout} data={data} />
      }
      case 'frequent': {
        return <FaqWidget key={layout.id} layout={layout as FrequentLayout} data={data} />
      }
      case 'category': {
        return (
          <CategoryWidget
            key={layout.id}
            layout={layout as CategoryLayout}
            data={data}
            path={path}
          />
        )
      }
      case 'icon': {
        return <IconWidget key={layout.id} layout={layout as IconLayout} />
      }
      case 'filterSearch': {
        return (
          <FilterSearchWidget
            key={layout.id}
            layout={layout as FilterSearchLayout}
            q={[]}
            data={data}
            path={path}
          />
        )
      }
      case 'filterCategory': {
        return (
          <FilterCategoryWidget
            key={layout.id}
            layout={layout as FilterCategoryLayout}
            dictionary={{}}
            pageContext={pageContext}
            data={data}
            path={path}
          />
        )
      }
      case 'html': {
        return <HtmlWidget key={layout.id} layout={layout as HtmlLayout} />
      }
      case 'iframe': {
        return <IframeWidget key={layout.id} layout={layout as IframeLayout} />
      }
      default: {
        return null
      }
    }
  })
}

export function renderSearchPageWidget<T extends SearchPageWidgetSettings>(
  layouts: readonly Layout[],
  props: RenderSearchPageWidgetProps<T>
) {
  return layouts.map(layout => {
    switch (layout.type) {
      case 'category': {
        return (
          <CategoryWidget
            key={layout.id}
            layout={layout as CategoryLayout}
            data={props.data}
            path={props.path}
          />
        )
      }
      case 'filterSearch': {
        return (
          <FilterSearchWidget
            key={layout.id}
            layout={layout as FilterSearchLayout}
            data={props.data}
            q={props.q}
            onSearch={props.onSearch}
            breadcrumbWidgetProps={{
              breadcrumb: props.breadcrumb,
              breadcrumbType: props.data.settingYaml.breadcrumbType,
              relatedCategories: props.pageContext?.relatedCategories || [],
              hierarchies: hierarchies(props.location),
              dictionary: props.dictionary,
              topTitle: topTitle(props.data.settingYaml.words),
            }}
            path={props.path}
          />
        )
      }
      case 'filterSearchOnly': {
        return (
          <FilterSearchOnlyWidget
            key={layout.id}
            layout={layout as FilterSearchOnlyLayout}
            data={props.data}
            location={props.location}
            q={props.q}
            onSearch={props.onSearch}
          />
        )
      }
      case 'breadcrumb': {
        return (
          <BreadcrumbWidget
            key={layout.id}
            breadcrumb={props.breadcrumb}
            breadcrumbType={props.data.settingYaml.breadcrumbType}
            relatedCategories={props.pageContext?.relatedCategories || []}
            hierarchies={hierarchies(props.location)}
            dictionary={props.dictionary}
            topTitle={topTitle(props.data.settingYaml.words)}
          />
        )
      }
      case 'filterSearchResult': {
        return (
          <FilterSearchResultWidget
            key={layout.id}
            layout={layout as FilterSearchResultLayout}
            dictionary={props.dictionary}
            data={props.data}
            q={props.q}
            page={props.page}
            onSearch={props.onSearch}
            pageContext={props.pageContext}
            botId={props.data.settingYaml.botId || ''}
            tenantId={props.data.settingYaml.tenantId || ''}
            path={props.path}
          />
        )
      }
      case 'filterCategory': {
        return (
          <FilterCategoryWidget
            key={layout.id}
            layout={layout as FilterCategoryLayout}
            dictionary={props.dictionary}
            pageContext={props.pageContext}
            q={props.q}
            data={props.data}
            path={props.path}
          />
        )
      }
      case 'html': {
        return <HtmlWidget key={layout.id} layout={layout as HtmlLayout} />
      }
      case 'iframe': {
        return <IframeWidget key={layout.id} layout={layout as IframeLayout} />
      }
      default: {
        return null
      }
    }
  })
}

export function renderTagPageWidget<T extends TagPageWidgetSettings>(
  layouts: readonly Layout[],
  props: RenderTagPageWidgetProps<T>
) {
  return layouts.map(layout => {
    switch (layout.type) {
      case 'navi': {
        return (
          <NaviWidget
            key={layout.id}
            botId={value(props.data.settingYaml.botId, '')}
            tenantId={value(props.data.settingYaml.tenantId, '')}
            breadcrumb={props.breadcrumb}
            breadcrumbType={props.data.settingYaml.breadcrumbType}
            relatedCategories={props.pageContext.relatedCategories || []}
            hierarchies={props.hierarchies}
            searchWaitMs={props.data.settingYaml.waitMs?.search ?? 5000}
            topTitle={topTitle(props.data.settingYaml.words)}
            searchType={props.data.settingYaml.searchType || ''}
            data={props.data}
          />
        )
      }
      case 'tag': {
        return <TagWidget key={layout.id} {...props} />
      }
      case 'breadcrumb': {
        return (
          <BreadcrumbWidget
            key={layout.id}
            breadcrumb={props.breadcrumb}
            breadcrumbType={props.data.settingYaml.breadcrumbType}
            relatedCategories={props.pageContext.relatedCategories || []}
            hierarchies={props.hierarchies}
            dictionary={props.dictionary}
            topTitle={topTitle(props.data.settingYaml.words)}
          />
        )
      }
      case 'category': {
        return (
          <CategoryWidget
            key={layout.id}
            layout={layout as CategoryLayout}
            data={props.data}
            path={props.path}
          />
        )
      }
      case 'search': {
        return <SearchWidget key={layout.id} layout={layout as SearchLayout} data={props.data} />
      }
      case 'html': {
        return <HtmlWidget key={layout.id} layout={layout as HtmlLayout} />
      }
      case 'iframe': {
        return <IframeWidget key={layout.id} layout={layout as IframeLayout} />
      }
      default: {
        return null
      }
    }
  })
}
